/**
 * wbRuntime — the first (and only) client-side JavaScript shipped on published
 * sites. A tiny, dependency-free progressive-enhancement script: it upgrades the
 * static `<audio controls>` fallback rendered by renderAudio into the custom
 * waveform player (play/pause toggle, progress fill via the `--wb-pct` CSS var,
 * click/keyboard seek). No bundler — it's a hand-written string written verbatim
 * to `/wb-runtime.js` by renderSiteClient and loaded `defer` only on pages that
 * contain a `[data-wb-audio]` widget.
 *
 * It reads nothing but `data-duration` and the audio element's own state — the
 * waveform SVG is baked at serialize time, so there's nothing to inject or eval.
 */

/** The published path/filename of the runtime script. */
export const WB_RUNTIME_FILE = "wb-runtime.js";

/** The verbatim contents of `/wb-runtime.js`. ES5, no dependencies. */
export const WB_RUNTIME_JS = `(function () {
  "use strict";
  function fmt(s) {
    if (!isFinite(s) || s <= 0) return "0:00";
    var m = Math.floor(s / 60);
    var sec = Math.floor(s % 60);
    return m + ":" + (sec < 10 ? "0" : "") + sec;
  }
  function hydrate(fig) {
    if (fig.getAttribute("data-wb-hydrated")) return;
    var audio = fig.querySelector(".wb-audio__native");
    var toggle = fig.querySelector(".wb-audio__toggle");
    var wave = fig.querySelector(".wb-audio__wave");
    var timeEl = fig.querySelector(".wb-audio__time");
    if (!audio || !toggle || !wave) return;
    fig.setAttribute("data-wb-hydrated", "1");
    fig.classList.add("wb-audio--ready");
    audio.removeAttribute("controls");
    var fallback = parseFloat(fig.getAttribute("data-duration")) || 0;
    function dur() {
      return audio.duration && isFinite(audio.duration)
        ? audio.duration
        : fallback;
    }
    function setPct(p) {
      p = Math.max(0, Math.min(100, p));
      fig.style.setProperty("--wb-pct", p + "%");
      wave.setAttribute("aria-valuenow", Math.round(p));
    }
    function render() {
      var d = dur();
      setPct(d > 0 ? (audio.currentTime / d) * 100 : 0);
      if (timeEl) timeEl.textContent = fmt(audio.currentTime) + " / " + fmt(d);
    }
    if (timeEl) timeEl.textContent = "0:00 / " + fmt(dur());
    toggle.addEventListener("click", function () {
      if (audio.paused) audio.play();
      else audio.pause();
    });
    audio.addEventListener("play", function () {
      toggle.textContent = "\\u23F8";
      toggle.setAttribute("aria-label", "Pause");
    });
    audio.addEventListener("pause", function () {
      toggle.textContent = "\\u25B6";
      toggle.setAttribute("aria-label", "Play");
    });
    audio.addEventListener("ended", function () {
      toggle.textContent = "\\u25B6";
      toggle.setAttribute("aria-label", "Play");
      setPct(0);
      if (timeEl) timeEl.textContent = "0:00 / " + fmt(dur());
    });
    audio.addEventListener("timeupdate", render);
    audio.addEventListener("loadedmetadata", render);
    wave.addEventListener("click", function (e) {
      var r = wave.getBoundingClientRect();
      var frac = (e.clientX - r.left) / r.width;
      frac = Math.max(0, Math.min(1, frac));
      var d = dur();
      if (d > 0) {
        audio.currentTime = frac * d;
        render();
      }
    });
    wave.addEventListener("keydown", function (e) {
      var d = dur();
      if (e.key === "ArrowRight") {
        if (d > 0) audio.currentTime = Math.min(d, audio.currentTime + 5);
        render();
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        audio.currentTime = Math.max(0, audio.currentTime - 5);
        render();
        e.preventDefault();
      } else if (e.key === " " || e.key === "Enter") {
        if (audio.paused) audio.play();
        else audio.pause();
        e.preventDefault();
      }
    });
  }
  var nodes = document.querySelectorAll("[data-wb-audio]");
  for (var i = 0; i < nodes.length; i++) hydrate(nodes[i]);
})();
`;
